import * as React from 'react';
import {
  IIFrameConfig,
  IOnBackendSocialLogin,
  IPrivacyStatus,
} from '../MemberLoginDialog.types';
import { IGNORED_ERRORS } from './utils';

export const useIDPPopup = (
  iframeConfig: IIFrameConfig,
  reportSocialAuthStarted: (idpConnectionId: string) => void,
  onBackendSocialLogin: IOnBackendSocialLogin,
  onError: (error: any) => void,
) => {
  const [prevBc, setPrevBc] = React.useState<BroadcastChannel | null>(null);

  return React.useCallback(
    async (idpConnectionId: string) => {
      reportSocialAuthStarted(idpConnectionId);
      prevBc?.close();
      /*
      Once social auth is completed, the server communicates the member's details and session token asynchronously via a BroadcastChannel.
      As this broadcast channel is readable by all sites on the same domain, and certainly by JS apps installed on this site, we:
      - generate a unique session ID for every login attempt. This protects us from accidental interference between sites or even multiple
        login attempts on the same site.
      - encrypt the data using a random key. This protects us from malicious JS apps installed on the site or other sites on the same
        domain.
      */
      const sessionId = window.crypto.randomUUID();
      const key = await window.crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt'],
      );
      const encryptionKey = uint8ToBase64(
        new Uint8Array(await window.crypto.subtle.exportKey('raw', key)),
      );

      const bc = new BroadcastChannel(`wix-idp-${sessionId}`);
      setPrevBc(bc);
      bc.addEventListener('message', async event => {
        const { data: eventData } = event;
        const { iv, data } = eventData;
        const decryptedData = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          key,
          data,
        );
        const loginResponse = JSON.parse(
          new TextDecoder().decode(decryptedData),
        );
        if (loginResponse.error) {
          if (!IGNORED_ERRORS.includes(loginResponse.error)) {
            onError(loginResponse.error);
          }
        } else {
          onBackendSocialLogin(
            JSON.parse(loginResponse.response),
            idpConnectionId,
            iframeConfig.isCommunityChecked,
          );
        }
        bc.postMessage(await encryptedCloseMessage(key));
        bc.close();
        setPrevBc(null);
      });

      const queryString = new URLSearchParams({
        visitorId: iframeConfig.biVisitorId,
        bsi: iframeConfig.bsi,
        svSession: iframeConfig.svSession,
        privacyStatus: getPrivacyStatus(iframeConfig.isCommunityChecked),
        tenantType: 'SITE',
        sessionId,
        encryptionKey,
      }).toString();
      const url = `/_api/iam/authentication/v1/sso/login/${iframeConfig.metaSiteId}/${idpConnectionId}?${queryString}`;
      window.open(url, 'oauthPopup', 'width=450,height=522');
    },
    [
      reportSocialAuthStarted,
      onBackendSocialLogin,
      onError,
      iframeConfig,
      prevBc,
    ],
  );
};

const uint8ToBase64 = (arr: Uint8Array): string =>
  btoa(
    Array(arr.length)
      .fill('')
      .map((_, i) => String.fromCharCode(arr[i]))
      .join(''),
  );

const encryptedCloseMessage = async (key: CryptoKey) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode('close'),
  );
  return { iv, data };
};

export const getPrivacyStatus = (
  isJoinCommunityChecked: boolean,
): IPrivacyStatus => {
  return isJoinCommunityChecked ? 'PUBLIC' : 'PRIVATE';
};
